import { formatNumber } from "@/lib/format";
import { TrafficSourceRow } from "../types/webstoreAnalytics";
import { Row, TablePanel, Td, Th } from "./AnalyticsTable";

interface TrafficSourceTableProps {
  sources: TrafficSourceRow[];
  limit?: number;
}

/**
 * Where visits came from.
 *
 * utm_source wins over the referrer when both are present: a tagged link states
 * intent, a referrer only observes. Neither means the visitor typed the address or
 * came from an app — shown as "Direct".
 *
 * Only the referrer host is ever stored, never the full URL, so there is no landing
 * page detail to expand here and never will be.
 */
export function TrafficSourceTable({ sources, limit = 12 }: TrafficSourceTableProps) {
  const shown = sources.slice(0, limit);

  return (
    <TablePanel
      title="Traffic sources"
      subtitle="UTM tag where present, otherwise the referring domain"
      isEmpty={shown.length === 0}
      empty="No sessions in this range"
      footer={sources.length > limit ? `Showing top ${limit} of ${sources.length}.` : undefined}
      head={
        <>
          <Th>Source</Th>
          <Th>Medium</Th>
          <Th>Campaign</Th>
          <Th align="right">Sessions</Th>
          <Th align="right">Visitors</Th>
          <Th align="right">Bounce</Th>
        </>
      }
    >
      {shown.map((source, index) => (
        <Row key={`${source.source}-${source.medium}-${source.campaign}-${index}`}>
          <Td className="font-medium text-slate-800">
            {source.source}
            <span className="text-slate-400 ml-1.5 num-mono text-[10px]">{source.share_pct}%</span>
          </Td>
          <Td className="text-slate-600">{source.medium}</Td>
          <Td className="text-slate-600 truncate max-w-[220px]">{source.campaign}</Td>
          <Td align="right">{formatNumber(source.sessions)}</Td>
          <Td align="right">{formatNumber(source.unique_visitors)}</Td>
          <Td align="right">
            {source.bounce_rate_pct == null ? "—" : `${source.bounce_rate_pct}%`}
          </Td>
        </Row>
      ))}
    </TablePanel>
  );
}
